import { Row, notification } from 'antd';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components/macro';

import { CommandBar } from '../../components/CommandBar';
import { PageTitle } from '../../components/PageTitle';
import { useRootStore } from '../../context/storeContext';
import { CommandsContainer } from '../Common/styles';
import { GlossaryFilters } from './components/GlossaryFilters';
import { GlossaryList } from './components/GlossaryList';
import { GlossaryListHeader } from './components/GlossaryListHeader';
import { GlossaryWordModal } from './components/GlossaryWordModal';

const GlossaryPageContainer = styled.div`
  width: 100%;
  padding-right: 46px;
`;

export const GlossaryPage = observer(() => {
  const { t } = useTranslation();
  const { glossaryStore } = useRootStore();

  const [showAddModal, setShowAddModal] = useState(false);
  const [isAddInProgress, setAddInProgress] = useState(false);

  useEffect(() => {
    return () => {
      glossaryStore.clearWords();
    };
  }, [glossaryStore]);

  const onAddClicked = useCallback(() => setShowAddModal(true), []);
  const onCloseAddModal = useCallback(() => setShowAddModal(false), []);

  const onSubmitAdd = useCallback(
    async (values: any, imageFile?: File | null) => {
      setAddInProgress(true);

      // Pre-generate the glossary ID so the image can be stored under the correct folder
      const glossaryId = crypto.randomUUID();
      let imageUrl = '';

      if (imageFile) {
        const uploadRes = await glossaryStore.uploadImage(glossaryId, imageFile);
        if (!uploadRes.succeeded) {
          setAddInProgress(false);
          notification.error({
            message: t('glossary:Notifications:imageUploadFailed'),
            description: uploadRes.message,
            placement: 'bottomRight',
            duration: 3,
          });
          return;
        }
        imageUrl = uploadRes.data || '';
      }

      const res = await glossaryStore.create({ ...values, id: glossaryId, imageUrl });
      setAddInProgress(false);
      if (res.succeeded) {
        setShowAddModal(false);
        notification.success({
          message: t('glossary:Notifications:createSuccess'),
          placement: 'bottomRight',
          duration: 2,
        });
      } else {
        notification.error({
          message: t('glossary:Notifications:createFailed'),
          description: res.message,
          placement: 'bottomRight',
          duration: 3,
        });
      }
    },
    [glossaryStore, t]
  );

  const commands = [
    {
      title: t('glossary:Commands:addNewWordLbl'),
      handler: onAddClicked,
    },
  ];

  return (
    <GlossaryPageContainer>
      <Row>
        <CommandsContainer>
          <PageTitle title={t('glossary:mainTitle')} />
          <CommandBar commands={commands} />
        </CommandsContainer>
      </Row>
      <Row gutter={8}>
        <GlossaryFilters />
      </Row>
      <Row>
        <GlossaryListHeader />
      </Row>
      <Row>
        <GlossaryList />
      </Row>

      <GlossaryWordModal
        isVisible={showAddModal}
        isLoading={isAddInProgress}
        editingWord={null}
        onClose={onCloseAddModal}
        onSubmit={onSubmitAdd}
      />
    </GlossaryPageContainer>
  );
});
